type Listener = (pref: SnowPreference) => void;

export type SnowPreference = 'on' | 'off';

const STORAGE_KEY = 'playerso_snow';
const listeners = new Set<Listener>();

/**
 * Account-level snow preference.
 *
 * Snow is no longer a third selectable theme — it follows dark mode. Users who
 * don't want it can switch it off in their account settings. That flag lives in
 * localStorage (same as the theme itself) and is broadcast to subscribers,
 * because same-tab `storage` events never fire.
 *
 * Defaults to `on`; the admin's `winter` entry in the theme settings is the
 * storefront-wide master switch that gates this further.
 */
export function readSnowPreference(): SnowPreference {
  if (typeof localStorage === 'undefined') return 'on';
  try {
    return localStorage.getItem(STORAGE_KEY) === 'off' ? 'off' : 'on';
  } catch {
    return 'on';
  }
}

export function persistSnowPreference(pref: SnowPreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* storage unavailable — preference applies for this session only */
  }
  for (const fn of listeners) fn(pref);
}

/**
 * Minimal notifier so the overlay (and any other consumer) can react to a
 * preference change without a storage event. Returns the unsubscribe fn.
 */
export const SnowPreference = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};